import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { AlertTriangle, Check, Crown, Gem, Sparkles } from "@fleet/icons";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import {
  billingQueryKeys,
  type components,
  evaluateQuotas,
  formatLimit,
  formatUsed,
  interpretCheckout,
  type QuotaEvaluation,
  type QuotaKey,
  resolvePurchaseSurface,
} from "@jornada/shared";
import { currentPurchasePlatform } from "@/lib/platform";
import { useI18n } from "@/providers/i18n-provider";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];
type CheckoutResponse = components["schemas"]["CheckoutResponse"];

/**
 * Plan/subscription screen (MONETIZATION_QUOTAS, B2B per-seat variant).
 *
 * - With monetization OFF (default), displays an empty state and nothing else:
 *   zero behavior change.
 * - With ON: current tier, REAL usage vs. per-quota limit bars (measured
 *   server-side; `collections` = supervised SEATS), warning when quota is
 *   exceeded or server rejected writes, team features included per
 *   tier, and upgrade via the injected PaymentProvider (with provider "none"
 *   upgrade is manual: assigned by an admin — without gateway).
 */
export function PlanOverview() {
  const { t } = useI18n();
  const session = useSession();

  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 30_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 15_000,
      enabled: !session.isVisitor() && configQuery.isSuccess && configQuery.data.enabled,
    }),
  );

  const checkoutMutation = createApiMutation(async (vars: { tier: string }) =>
    // The surface travels in the request: the server does not return a payment
    // URL to a store binary (`monetization::purchase`). Always sending the field
    // is what lets its ABSENCE fail closed there.
    api.POST("/api/billing/checkout", {
      body: { tier: vars.tier, platform: currentPurchasePlatform() },
    }),
  );

  const config = () => (configQuery.isSuccess ? configQuery.data : undefined);
  const usage = () => (usageQuery.isSuccess ? usageQuery.data : undefined);

  /**
   * PLATFORM CLAMP — the client-side half.
   *
   * Without this, three admin-panel rows (`monetization.enabled`,
   * `payments.enabled`, `payments.provider=stripe`) make an **already-installed
   * APK** render “Upgrade” and navigate to Stripe: a Google Play removal reason,
   * without redeploying or re-uploading. The server already blocks it; this closes
   * the other half because the published binary does not learn that the server
   * changed and because a button that always fails is itself a purchase surface
   * visible inside the binary.
   */
  const purchaseSurface = createMemo(() =>
    resolvePurchaseSurface({
      platform: currentPurchasePlatform(),
      enabled: config()?.enabled ?? false,
      paymentsEnabled: config()?.paymentsEnabled ?? false,
      provider: config()?.provider ?? "none",
    }),
  );

  const currentTier = createMemo(() => {
    const id = usage()?.tier ?? config()?.defaultTier;
    return config()?.tiers.find((tier) => tier.id === id);
  });

  const evaluations = createMemo<QuotaEvaluation[]>(() => {
    const u = usage();
    if (!u) {
      return [];
    }
    return evaluateQuotas(
      {
        storage_bytes: u.limits.storageBytes ?? null,
        items: u.limits.maxItems ?? null,
        collections: u.limits.maxCollections ?? null,
        orgs: u.limits.maxOrgs ?? null,
        attachments: u.limits.maxAttachments ?? null,
      },
      {
        storage_bytes: u.usage.storageBytes,
        items: u.usage.items,
        collections: u.usage.collections,
        orgs: u.usage.orgs,
        attachments: u.usage.attachments,
      },
    );
  });

  const hasExceeded = () => (usage()?.exceeded.length ?? 0) > 0;
  const hasRejections = () => (usage()?.rejectedCount ?? 0) > 0;

  const quotaLabel = (key: QuotaKey): string => {
    switch (key) {
      case "storage_bytes": {
        return t(msg`Storage`);
      }
      case "items": {
        return t(msg`Proposed time entries`);
      }
      case "collections": {
        return t(msg`Seats (employees)`);
      }
      case "orgs": {
        return t(msg`Organizations`);
      }
      default: {
        return t(msg`Attachments`);
      }
    }
  };

  const featureLabel = (feature: string): string => {
    switch (feature) {
      case "team_reports": {
        return t(msg`Team reports`);
      }
      case "time_tracker_bridge": {
        return t(msg`Time-tracker integration`);
      }
      default: {
        return feature;
      }
    }
  };

  const startUpgrade = (tierId: string) => {
    checkoutMutation.mutate(
      { tier: tierId },
      {
        onSuccess: (result: CheckoutResponse) => {
          // `interpretCheckout` REQUIRES the platform: it does not compile
          // without it. This is the final gate before `location.assign`, and it
          // returns `blocked` when the surface does not allow that purchase.
          const action = interpretCheckout(
            {
              provider: result.provider,
              checkoutUrl: result.checkoutUrl ?? null,
              manual: result.manual,
            },
            currentPurchasePlatform(),
          );
          if (action.kind === "redirect") {
            globalThis.location.assign(action.url);
          } else if (action.kind === "blocked") {
            showToast(
              t(msg`Purchases are not available in this app. Manage your plan on the website.`),
              "info",
            );
          } else {
            showToast(
              t(msg`Upgrades are handled manually. Please contact support to change your plan.`),
              "info",
            );
          }
        },
        onError: () => {
          showToast(t(msg`Could not start the upgrade. Try again later.`), "error");
        },
      },
    );
  };

  return (
    <div class="space-y-6" data-testid="plan-overview">
      <Show
        when={!configQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <Skeleton class="h-24 w-full rounded-xl" />
            <Skeleton class="h-48 w-full rounded-xl" />
          </div>
        }
      >
        <Show
          when={config()?.enabled}
          fallback={
            <EmptyState
              icon={Gem}
              title={t(msg`Plans are not available yet`)}
              description={t(msg`This app does not have subscription plans enabled.`)}
            />
          }
        >
          {/* Tier actual */}
          <Card data-testid="plan-current-tier">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <Crown class="h-5 w-5 text-primary" />
                {t(msg`Your plan`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="flex items-center justify-between gap-4">
              <div>
                <div class="text-2xl font-bold" data-testid="plan-tier-label">
                  {currentTier()?.label ?? config()?.defaultTier}
                </div>
                <p class="text-xs text-muted-foreground">
                  {t(msg`Per-seat plan: each supervised employee profile uses one seat.`)}
                </p>
              </div>
              <Show when={(currentTier()?.features?.length ?? 0) > 0}>
                <Badge variant="secondary">{t(msg`Team features`)}</Badge>
              </Show>
            </CardContent>
          </Card>

          {/* Señal de quota excedida / escrituras rechazadas */}
          <Show when={hasExceeded() || hasRejections()}>
            <Card class="border-destructive/50" data-testid="quota-exceeded-banner">
              <CardContent class="p-4 flex items-start gap-3">
                <AlertTriangle class="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div class="min-w-0 text-sm">
                  <p class="font-semibold">{t(msg`You reached a limit of your plan`)}</p>
                  <p class="text-muted-foreground">
                    <Show
                      when={hasRejections()}
                      fallback={t(msg`New items may not sync until you free up space or upgrade.`)}
                    >
                      {t(
                        msg`Some recent changes could not be saved to the server. Free up space or upgrade your plan.`,
                      )}
                    </Show>
                  </p>
                </div>
              </CardContent>
            </Card>
          </Show>

          {/* Uso vs límites (usuarios con cuenta) */}
          <Show
            when={!session.isVisitor()}
            fallback={
              <Card>
                <CardContent class="p-6 text-sm text-muted-foreground">
                  {t(msg`Create an account to track your usage across devices.`)}
                </CardContent>
              </Card>
            }
          >
            <Card data-testid="plan-usage">
              <CardHeader>
                <CardTitle>{t(msg`Usage`)}</CardTitle>
              </CardHeader>
              <CardContent class="space-y-4">
                <Show
                  when={!usageQuery.isLoading}
                  fallback={
                    <div class="space-y-3">
                      <Skeleton class="h-8 w-full rounded" />
                      <Skeleton class="h-8 w-full rounded" />
                      <Skeleton class="h-8 w-full rounded" />
                    </div>
                  }
                >
                  <For each={evaluations()}>
                    {(evaluation) => (
                      <div class="space-y-1" data-testid={`quota-row-${evaluation.key}`}>
                        <div class="flex items-center justify-between text-sm">
                          <span class="font-medium">{quotaLabel(evaluation.key)}</span>
                          <span
                            classList={{
                              "text-muted-foreground": !evaluation.nearLimit,
                              "text-warning": evaluation.nearLimit && !evaluation.exceeded,
                              "text-destructive font-semibold": evaluation.exceeded,
                            }}
                            data-testid={`quota-value-${evaluation.key}`}
                          >
                            {formatUsed(evaluation.key, evaluation.used)} /{" "}
                            {formatLimit(evaluation.key, evaluation.limit)}
                          </span>
                        </div>
                        <div class="h-2 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full transition-all"
                            classList={{
                              "bg-primary": !evaluation.nearLimit,
                              "bg-warning": evaluation.nearLimit && !evaluation.exceeded,
                              "bg-destructive": evaluation.exceeded,
                            }}
                            style={{ width: `${Math.round(evaluation.ratio * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </For>
                </Show>
              </CardContent>
            </Card>
          </Show>

          {/* Tiers disponibles + upgrade */}
          <Card data-testid="plan-tiers">
            <CardHeader>
              <CardTitle class="flex items-center gap-2">
                <Sparkles class="h-5 w-5 text-primary" />
                {t(msg`Available plans`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="grid gap-3 sm:grid-cols-2">
              <For each={config()?.tiers ?? []}>
                {(tier) => (
                  <div
                    class="rounded-xl border p-4 space-y-2"
                    classList={{
                      "border-primary": tier.id === (usage()?.tier ?? config()?.defaultTier),
                    }}
                    data-testid={`plan-tier-${tier.id}`}
                  >
                    <div class="flex items-center justify-between">
                      <span class="font-bold">{tier.label}</span>
                      <Show when={tier.id === (usage()?.tier ?? config()?.defaultTier)}>
                        <Badge>{t(msg`Current`)}</Badge>
                      </Show>
                    </div>
                    <ul class="text-xs text-muted-foreground space-y-1">
                      <li>
                        {t(msg`Seats (employees)`)}:{" "}
                        {formatLimit("collections", tier.limits.maxCollections ?? null)}
                      </li>
                      <li>
                        {t(msg`Storage`)}:{" "}
                        {formatLimit("storage_bytes", tier.limits.storageBytes ?? null)}
                      </li>
                      <For each={tier.features ?? []}>
                        {(feature) => (
                          <li class="flex items-center gap-1">
                            <Check class="h-3 w-3 text-success shrink-0" />
                            {featureLabel(feature)}
                          </li>
                        )}
                      </For>
                    </ul>
                    {/* El botón solo existe donde la compra es legal: en el
                        binario de tienda no se pinta «Upgrade». */}
                    <Show
                      when={
                        purchaseSurface().offersPurchase &&
                        !session.isVisitor() &&
                        tier.id !== (usage()?.tier ?? config()?.defaultTier)
                      }
                    >
                      <Button
                        size="sm"
                        class="w-full"
                        disabled={checkoutMutation.isPending}
                        onClick={() => {
                          startUpgrade(tier.id);
                        }}
                        data-testid={`plan-upgrade-${tier.id}`}
                      >
                        {t(msg`Upgrade`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
